'use client'

import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'

export interface WidgetState {
    from: string
    fromId: string
    to: string
    toId: string
    departDate: string
    returnDate?: string
    mode: 'bus' | 'train' | 'ferry' | 'flight'
    tripType: 'one-way' | 'round-trip'
    /** Seat count (1–8). Mirrors ui-react's multi-pax `?p=N` search param. */
    passengers: number
}

interface StoredWidgetState {
    state: WidgetState
    timestamp: number
}

const STORAGE_KEY = 'wanderu-widget-state'
const TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

const defaultState: WidgetState = {
    from: '',
    fromId: '',
    to: '',
    toId: '',
    departDate: '',
    mode: 'bus',
    tripType: 'one-way',
    passengers: 1,
}

/** Returns today's date as YYYY-MM-DD */
function getTodayISO(): string {
    const d = new Date()
    const y = String(d.getFullYear())
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

/** Adjust dates so departDate is not in the past and returnDate >= departDate */
function adjustDates(state: WidgetState): WidgetState {
    const today = getTodayISO()

    if (!state.departDate || state.departDate < today) {
        state = { ...state, departDate: today }
    }

    if (state.returnDate && state.returnDate < state.departDate) {
        state = { ...state, returnDate: undefined }
    }

    return state
}

/** Read stored state from localStorage with TTL check */
function readStoredState(): WidgetState | null {
    if (typeof window === 'undefined') {
        return null
    }

    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
        return null
    }

    try {
        const parsed = JSON.parse(raw) as StoredWidgetState | WidgetState

        // Support both old format (plain state) and new format (with timestamp)
        if ('timestamp' in parsed && 'state' in parsed) {
            if (Date.now() - parsed.timestamp > TTL_MS) {
                localStorage.removeItem(STORAGE_KEY)
                return null
            }
            return parsed.state
        }

        // Legacy format: plain state without timestamp — treat as valid but migrate
        return parsed
    } catch {
        localStorage.removeItem(STORAGE_KEY)
        return null
    }
}

/** Enforce returnDate >= departDate on every state update */
function enforceReturnDate(state: WidgetState): WidgetState {
    if (state.returnDate && state.departDate && state.returnDate < state.departDate) {
        return { ...state, returnDate: state.departDate }
    }
    return state
}

/**
 * Drop keys whose value is `undefined` so a partial object passed by a caller
 * (e.g. `{ from: undefined }` from a widget mounted with no initial values)
 * doesn't clobber the string defaults during a spread merge.
 */
function definedValues<T extends object>(obj: T | undefined): Partial<T> {
    if (!obj) {
        return {}
    }
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined),
    ) as Partial<T>
}

export function useWidgetState(initial?: Partial<WidgetState>) {
    const initialRef = useRef(initial)

    const [state, rawSetState] = useState<WidgetState>(() => {
        // Server-safe: use only defaults + initial props (no localStorage)
        return adjustDates({ ...defaultState, ...definedValues(initial) })
    })

    const setState = useCallback((updater: SetStateAction<WidgetState>) => {
        rawSetState((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater
            return enforceReturnDate(next)
        })
    }, [])

    // Hydrate from localStorage after mount to avoid SSR mismatch
    useEffect(() => {
        const stored = readStoredState()
        if (stored) {
            rawSetState(
                adjustDates({ ...defaultState, ...stored, ...definedValues(initialRef.current) }),
            )
        }
    }, [])

    useEffect(() => {
        const entry: StoredWidgetState = {
            state,
            timestamp: Date.now(),
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
    }, [state])

    return [state, setState] as const
}
